import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card, Button, Space, Tag, Select, Modal, Drawer, Form, Input, InputNumber,
  message, Tooltip, Collapse, Typography, Grid, Checkbox,
} from "antd";
import {
  PlusOutlined, CheckOutlined, StopOutlined, ReloadOutlined, RollbackOutlined,
  EyeOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined,
  LeftOutlined, RightOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  scGetEvents, scCreateEvent, scUpdateEvent, scDeleteEvent, scApproveEvent, scCancelEvent,
  scRestoreEvent, scGetParties, scGetItems, scGetBatches, scYarnPurchasesAvailable,
  scBulkUpdateEvents, scBulkDeleteEvents,
} from "../../api/client";
import { JalaliDateInput, todayISO, toJalali, toJalaliDateTime } from "../../utils/dateUtils";
import { byStr, byNum, byDate } from "../../utils/sorters";
import { useAuth } from "../../context/AuthContext";
import { FILTERABLE_EVENT_TYPES, eventLabel, eventColor, eventCategoryLabel, statusMeta, STATUS_META, fmtKg, fmtRial } from "./scConstants";
import { MOVEMENT_TEMPLATES, getTemplate, MOVEMENT_GUIDE, PRIMARY_MOVEMENT_TYPES, PRIMARY_TYPE_VALUES } from "./movementTemplates";
import { enabledCategories, eventTypesForCategory, categoryOfEventType, newInvoicePath } from "./supplyCategories";
import useColorOptions from "../../hooks/useColorOptions";
import { saveInvoiceListCtx } from "../../utils/invoiceListCtx";
import EditableTable from "../../components/EditableTable";
import RowActions from "../../components/RowActions";
import { faNum } from "../../utils/gxSummary";
import { exportEventsForImport } from "../../utils/scEventsExport";
import PartyPicker from "./PartyPicker";
import ScExportModal from "./ScExportModal";

const { Option } = Select;
const { Text } = Typography;
const { useBreakpoint } = Grid;
const PARTY_FIELDS = ["source_party_id", "destination_party_id", "current_holder_party_id"];

export default function MovementEventsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = ساخت، عدد = ویرایش
  const [editIndex, setEditIndex] = useState(-1);     // جایگاهِ رکوردِ در حالِ ویرایش در لیست (برای قبلی/بعدی)
  const snapRef = useRef({});                         // عکسِ مقادیرِ بارگذاری‌شده (برای تشخیصِ تغییرِ واقعی)
  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const filteredRows = useMemo(() => (
    filters.q_invoice
      ? rows.filter((r) => String(r.external_invoice_number || "").includes(filters.q_invoice))
      : rows
  ), [rows, filters.q_invoice]);

  const openWorkspace = (r) => {
    const ids = filteredRows.map((x) => x.id);
    const index = ids.indexOf(r.id);
    const ctx = {
      ids,
      index: index >= 0 ? index : 0,
      returnTo: "/supply-chain/events",
      filters,
    };
    saveInvoiceListCtx(ctx);
    navigate(`/supply-chain/events/${r.id}`, { state: ctx });
  };
  const [form] = Form.useForm();
  const evType = Form.useWatch("event_type", form);
  const wWeight = Form.useWatch("weight_kg", form);
  const wPrice = Form.useWatch("unit_price", form);
  const wOverride = Form.useWatch("manual_total_override", form);
  const wTotal = Form.useWatch("total_amount", form);
  const tpl = getTemplate(evType);

  // گزینه‌های «نوع حرکت»: فقط انواعِ اصلی؛ هنگامِ ویرایشِ رکوردِ قدیمی، نوعِ خودش هم اضافه می‌شود
  const typeOptions = useMemo(() => {
    const opts = PRIMARY_MOVEMENT_TYPES.map((t) => ({ value: t.v, label: t.l }));
    if (editingId && evType && !PRIMARY_TYPE_VALUES.includes(evType))
      opts.push({ value: evType, label: eventLabel(evType) + " (قدیمی)" });
    return opts;
  }, [editingId, evType]);

  // محاسبهٔ خودکارِ مبلغ: جمع = وزن × فی (مگر «ویرایش دستی مبلغ» فعال باشد)
  useEffect(() => {
    if (!wOverride && wWeight && wPrice) {
      form.setFieldsValue({ total_amount: Math.round(Number(wWeight) * Number(wPrice)) });
    }
    // eslint-disable-next-line
  }, [wWeight, wPrice, wOverride]);

  const [purchases, setPurchases] = useState([]);
  const loadRefs = async () => {
    try {
      const [p, i, b] = await Promise.all([scGetParties(), scGetItems(), scGetBatches()]);
      setParties(p.data); setItems(i.data); setBatches(b.data);
    } catch { /* silent */ }
  };
  const loadPurchases = async () => {
    try { const r = await scYarnPurchasesAvailable(); setPurchases(r.data); } catch { /* silent */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const { _category, _catTypes, q_invoice, ...apiFilters } = filters;
      const r = await scGetEvents(apiFilters);
      let data = r.data;
      if (_catTypes && _catTypes.length && !apiFilters.event_type) {
        data = data.filter((x) => _catTypes.includes(x.event_type));
      }
      setRows(data);
    }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در بارگذاری"); }
    setLoading(false);
  };
  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(filters)]);

  const partyOfType = (ptype) => parties.find((p) => p.party_type === ptype);

  const openNew = (preset) => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ event_type: preset || "yarn_purchased", event_date: todayISO() });
    loadPurchases();
    setOpen(true);
  };
  // عکس‌گرفتن از مقادیرِ فعلیِ فرم پس از تثبیت (تاریخِ شمسی async نرمال می‌شود)
  const takeSnapshot = () => { setTimeout(() => { snapRef.current = form.getFieldsValue(); }, 0); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setEditIndex(rows.findIndex((r) => r.id === row.id));
    form.resetFields();
    form.setFieldsValue({ ...row, reason: undefined });
    takeSnapshot();
    loadPurchases();
    setOpen(true);
  };
  // بارگذاریِ رکوردِ i از لیست در همان فرم (برای ناوبریِ قبلی/بعدی)
  const loadRowAt = (i) => {
    const r = rows[i];
    if (!r) return;
    setEditingId(r.id); setEditIndex(i);
    form.resetFields();
    form.setFieldsValue({ ...r, reason: undefined });
    takeSnapshot();
  };
  // تغییرِ واقعی؟ مقایسهٔ مقادیرِ فعلیِ فرم با عکسِ بارگذاری (نه isFieldsTouched که با تاریخِ شمسی false-positive می‌دهد)
  const isDirty = () => {
    const cur = form.getFieldsValue();
    const snap = snapRef.current || {};
    const keys = new Set([...Object.keys(cur), ...Object.keys(snap)]);
    for (const k of keys) {
      if (JSON.stringify(cur[k] ?? null) !== JSON.stringify(snap[k] ?? null)) return true;
    }
    return false;
  };
  // انتخابِ سندِ خرید → auto-fill نوع نخ/مانده/کارتن/پالت/مجوز
  const onPickPurchase = (pid) => {
    const p = purchases.find((x) => x.id === pid);
    if (p) form.setFieldsValue({
      yarn_count: p.yarn_count || undefined, weight_kg: p.remaining_kg,
      carton_count: p.carton_count || undefined, pallet_count: p.pallet_count || undefined,
      permit_number: p.permit_number || undefined,
    });
  };

  // لینک‌های قدیمی ?new=<event_type> → صفحهٔ ثبت فاکتور
  useEffect(() => {
    const n = searchParams.get("new");
    if (n && MOVEMENT_TEMPLATES[n]) {
      const cat = categoryOfEventType(n);
      navigate(newInvoicePath(cat?.key, n), { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // drill-down از داشبورد: seedِ فیلترها از query-param (یک‌بار، هنگام ورود)
  useEffect(() => {
    const seed = {};
    ["event_type", "status"].forEach((k) => { const v = searchParams.get(k); if (v) seed[k] = v; });
    const pid = searchParams.get("party_id"); if (pid) seed.party_id = Number(pid);
    const df = searchParams.get("date_from"); if (df) seed.date_from = df;
    const dt = searchParams.get("date_to"); if (dt) seed.date_to = dt;
    if (Object.keys(seed).length) {
      setFilters((f) => ({ ...f, ...seed }));
      ["event_type", "status", "party_id", "date_from", "date_to"].forEach((k) => searchParams.delete(k));
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // ذخیره؛ در صورت موفقیت true برمی‌گرداند و لیست را (منتظر) تازه می‌کند
  const doSave = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return false; }
    const payload = { ...v, event_date: v.event_date || todayISO() };
    try {
      if (editingId) {
        await scUpdateEvent(editingId, payload);
        message.success("رویداد ویرایش شد");
      } else {
        await scCreateEvent(payload);
        message.success("به‌صورت پیش‌نویس ثبت شد — برای ثبت در دفتر، «تأیید» کنید");
      }
      await load();
      return true;
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ثبت"); return false; }
  };
  const submit = async () => { if (await doSave()) setOpen(false); };

  // ناوبریِ قبلی/بعدی بین رکوردها (مثلِ حسابداری). اگر تغییرِ ذخیره‌نشده بود می‌پرسد.
  const goEdit = (delta) => {
    const t = editIndex + delta;
    if (t < 0 || t >= rows.length) return;
    if (isDirty()) {
      Modal.confirm({
        title: "تغییراتِ ذخیره‌نشده",
        content: "این رکورد تغییر کرده. ذخیره شود؟",
        okText: "ذخیره و برو", cancelText: "بدون ذخیره، برو",
        keyboard: false, maskClosable: false,
        onOk: async () => { if (await doSave()) loadRowAt(t); },
        onCancel: () => loadRowAt(t),
      });
    } else loadRowAt(t);
  };
  const saveAndNext = async () => {
    if (await doSave()) {
      const t = editIndex + 1;
      if (t < rows.length) loadRowAt(t); else setOpen(false);
    }
  };
  // میان‌بر: Alt+← بعدی، Alt+→ قبلی (RTL)
  useEffect(() => {
    if (!open || editingId == null) return undefined;
    const h = (e) => {
      if (!e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); goEdit(1); }
      if (e.key === "ArrowRight") { e.preventDefault(); goEdit(-1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  const removeEvent = async (id) => {
    try { await scDeleteEvent(id); message.success("حذف شد"); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در حذف"); }
  };

  const approve = async (id) => {
    try { await scApproveEvent(id); message.success("تأیید و در دفتر امانی ثبت شد"); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در تأیید"); }
  };
  const cancel = async (id) => {
    try { await scCancelEvent(id, "لغو دستی"); message.success("رویداد لغو شد"); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در لغو"); }
  };
  const restore = async (id) => {
    try { await scRestoreEvent(id); message.success("بازگردانده شد (پیش‌نویس)"); load(); }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در بازگردانی"); }
  };

  // شرح = «چه چیزی خریده/جابه‌جا شده»: کالا+رنگ یا نوعِ نخ یا نوعِ پارچه
  const itemDesc = (r) =>
    r.item_name
    || [r.fabric_type || r.yarn_type, r.color].filter(Boolean).join(" — ")
    || (r.yarn_count ? `نخ ${r.yarn_count}` : "")
    || (r.notes && !r.notes.startsWith("ایمپورت از") ? r.notes : "")
    || "—";

  // مجوزِ ویرایشِ هر ردیف: رکوردِ تأییدشده فقط با «sc.event.edit_approved» (رفعِ 403 غیرمنتظره در UI)
  const canEditEventRow = (r) => {
    if (r.status === "cancelled") return "رویدادِ لغوشده قابلِ ویرایش نیست؛ اول بازگردانی کنید";
    if (r.status === "confirmed") {
      return can("sc.event.edit_approved") ? true : "ویرایشِ رویدادِ تأییدشده نیازمند دسترسی مدیر است";
    }
    return can("sc.event.create") ? true : "دسترسیِ ویرایش ندارید";
  };

  const partyOptions = useMemo(
    () => parties.map((p) => ({ value: p.id, label: p.name })),
    [parties],
  );
  const partyOptionsByTypes = (types) => partyOptions.filter((o) => {
    const p = parties.find((x) => x.id === o.value);
    return !types || (p && types.includes(p.party_type));
  });

  // رنگ‌ها از کاتالوگِ مشترک می‌آیند (همان لیستی که ثبتِ تولید هم می‌بیند)؛
  // رنگ‌های دادهٔ همین لیست هم اضافه می‌شوند تا مقدارِ قدیمی گم نشود.
  const { colorOptions, registerColor } = useColorOptions(
    useMemo(() => (rows || []).map((r) => r.color).filter(Boolean), [rows]));

  const eventTypeEditOptions = (row) => {
    const opts = PRIMARY_MOVEMENT_TYPES.map((t) => ({ value: t.v, label: t.l }));
    // ضایعات هم قابل ویرایش گروهی
    if (!opts.some((o) => o.value === "waste_registered")) {
      opts.push({ value: "waste_registered", label: "ثبت ضایعات" });
    }
    const et = row?.event_type;
    if (et && !opts.some((o) => o.value === et)) {
      opts.push({ value: et, label: `${eventLabel(et)} (قدیمی)` });
    }
    return opts;
  };

  const sourceKind = (r) => (r.imported_at || String(r.notes || "").startsWith("ایمپورت از") ? "ایمپورت" : "دستی");
  const columns = [
    { title: "تاریخ", dataIndex: "event_date", width: 105, sorter: byDate("event_date"), defaultSortOrder: "descend",
      editType: "date", gxFilter: "dateRange",
      render: (v) => toJalali(v),
      exportValue: (v) => toJalali(v) },
    { title: "دسته", key: "category", width: 100,
      sorter: (a, b) => String(eventCategoryLabel(a.event_type)).localeCompare(String(eventCategoryLabel(b.event_type)), "fa"),
      gxFilter: "enum",
      gxFilterOptions: [
        ...enabledCategories().map((c) => ({ text: c.label, value: c.key })),
        { text: "سایر", value: "_other" },
      ],
      gxFilterGet: (r) => categoryOfEventType(r.event_type)?.key || "_other",
      render: (_, r) => eventCategoryLabel(r.event_type) },
    { title: "نوع عملیات", dataIndex: "event_type", width: 150, ellipsis: true, sorter: byStr("event_type"),
      editType: "select", editAllowClear: false,
      options: (row) => eventTypeEditOptions(row),
      gxFilter: "enum",
      gxFilterOptions: FILTERABLE_EVENT_TYPES.map((e) => ({ text: e.l, value: e.v })),
      render: (v) => <Tag color={eventColor(v)}>{eventLabel(v)}</Tag>,
      exportValue: (v) => eventLabel(v) },
    { title: "شماره فاکتور", dataIndex: "external_invoice_number", width: 115, sorter: byStr("external_invoice_number"),
      editType: "text", gxFilter: "text", render: (v) => v || "—" },
    { title: "طرف حساب", dataIndex: "source_party_id", width: 130, ellipsis: true,
      editType: "select", editAllowClear: true,
      sorter: (a, b) => String(a.source_party_name || "").localeCompare(String(b.source_party_name || ""), "fa"),
      options: (row) => {
        const tplR = getTemplate(row?.event_type);
        if (tplR.sourceSelect) return partyOptionsByTypes(tplR.sourceSelect.types);
        if (tplR.fixedSourceType) return partyOptionsByTypes([tplR.fixedSourceType]);
        return partyOptions;
      },
      gxFilter: "enum",
      gxFilterGet: (r) => r.source_party_name || "",
      render: (_, r) => r.source_party_name || "—",
      exportValue: (_, r) => r.source_party_name || "" },
    { title: "مقصد", dataIndex: "destination_party_id", width: 120, ellipsis: true,
      editType: "select", editAllowClear: true,
      sorter: (a, b) => String(a.destination_party_name || "").localeCompare(String(b.destination_party_name || ""), "fa"),
      options: (row) => {
        const tplR = getTemplate(row?.event_type);
        if (tplR.destSelect) return partyOptionsByTypes(tplR.destSelect.types);
        if (tplR.fixedDestType) return partyOptionsByTypes([tplR.fixedDestType]);
        return partyOptions;
      },
      gxFilter: "enum",
      gxFilterGet: (r) => r.destination_party_name || "",
      render: (_, r) => r.destination_party_name || "—",
      exportValue: (_, r) => r.destination_party_name || "" },
    { title: "شرح", dataIndex: "item_name", width: 180, ellipsis: true, sorter: byStr("item_name"),
      editType: "textarea", gxFilter: "text",
      render: (_, r) => itemDesc(r),
      exportValue: (_, r) => itemDesc(r) },
    { title: "رنگ", dataIndex: "color", width: 90, sorter: byStr("color"),
      // رنگِ تازه هم پذیرفته می‌شود: تایپ کن و Enter بزن
      editType: "select", editAllowClear: true, editAllowNew: true, options: colorOptions,
      editOnCreate: registerColor,
      gxFilter: "enum",
      gxFilterOptions: colorOptions.map((c) => ({ text: c.label, value: c.value })),
      render: (v) => v || "—" },
    { title: "نوع پارچه", dataIndex: "fabric_type", width: 120, ellipsis: true, sorter: byStr("fabric_type"),
      editType: "text", gxFilter: "enum",
      render: (v) => v || "—" },
    { title: "شماره سفارش", dataIndex: "order_code", width: 100, sorter: byStr("order_code"),
      editType: "text", gxFilter: "text", render: (v) => v || "—" },
    { title: "شماره سند", dataIndex: "external_document_number", width: 100, sorter: byStr("external_document_number"),
      editType: "text", gxFilter: "text", render: (v) => v || "—" },
    { title: "مجوز", dataIndex: "permit_number", width: 90, sorter: byStr("permit_number"),
      editType: "text", gxFilter: "text", render: (v) => v || "—" },
    { title: "وزن", dataIndex: "weight_kg", width: 100, align: "center", sorter: byNum("weight_kg"),
      editType: "number", gxFilter: "num",
      editRule: (v) => (v !== null && v !== undefined && Number(v) <= 0 ? "وزن باید بزرگ‌تر از صفر باشد" : null),
      render: (v) => fmtKg(v) },
    { title: "طاقه", dataIndex: "roll_count", width: 70, align: "center", sorter: byNum("roll_count"),
      editType: "number", gxFilter: "num", render: (v) => (v != null ? v : "—") },
    { title: "کارتن", dataIndex: "carton_count", width: 70, align: "center", sorter: byNum("carton_count"),
      editType: "number", gxFilter: "num", render: (v) => (v != null ? v : "—") },
    { title: "فی (ریال)", dataIndex: "unit_price", width: 110, align: "center", sorter: byNum("unit_price"),
      editType: "number", gxFilter: "num",
      editRule: (v) => (v !== null && v !== undefined && Number(v) < 0 ? "فی نمی‌تواند منفی باشد" : null),
      render: (v) => (v ? fmtRial(v) : "—") },
    { title: "مبلغ", dataIndex: "total_amount", width: 120, align: "center", sorter: byNum("total_amount"),
      gxFilter: "num",
      render: (v) => (v ? fmtRial(v) : "—") },
    { title: "منبع", key: "source_kind", width: 90,
      sorter: (a, b) => sourceKind(a).localeCompare(sourceKind(b), "fa"),
      gxFilter: "enum",
      gxFilterOptions: [{ text: "ایمپورت", value: "ایمپورت" }, { text: "دستی", value: "دستی" }],
      gxFilterGet: (r) => sourceKind(r),
      render: (_, r) => sourceKind(r) },
    // زمانِ ایمپورتِ فایل (کنارِ تاریخِ خودِ فاکتور) — برای ثبتِ دستی خالی
    { title: "تاریخ ایمپورت", dataIndex: "imported_at", width: 130, sorter: byStr("imported_at"),
      gxFilter: "dateRange",
      render: (v, r) => (v
        ? <Tooltip title={r.import_file ? `فایل: ${r.import_file}` : undefined}><span>{toJalaliDateTime(v)}</span></Tooltip>
        : "—"),
      exportValue: (v) => (v ? toJalaliDateTime(v) : "") },
    { title: "وضعیت", dataIndex: "status", width: 140, sorter: byStr("status"),
      editType: "select", editAllowClear: false,
      options: Object.entries(STATUS_META)
        .filter(([v]) => v !== "cancelled")
        .map(([v, m]) => ({ value: v, label: m.l })),
      gxFilter: "enum",
      gxFilterOptions: Object.entries(STATUS_META).map(([v, m]) => ({ text: m.l, value: v })),
      render: (v, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={statusMeta(v).c}>{statusMeta(v).l}</Tag>
          {r.was_edited && <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>ویرایش‌شده</Tag>}
          {r.needs_completion && (
            <Tooltip title={"جامانده: " + ((r.missing_fields || []).join("، ") || "—")}>
              <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>نیازمند تکمیل</Tag>
            </Tooltip>
          )}
        </Space>
      ),
      exportValue: (v) => statusMeta(v).l },
    {
      title: "اقدام", key: "actions", width: isMobile ? 56 : 240, fixed: "right", align: "center",
      gxFilter: false, gxExport: false,
      render: (_, r) => (
        <RowActions actions={[
          { key: "view", label: "مشاهده", icon: <EyeOutlined />, onClick: () => openWorkspace(r) },
          { key: "edit", label: "ویرایش", icon: <EditOutlined />, onClick: () => openWorkspace(r),
            hidden: canEditEventRow(r) !== true },
          { key: "restore", label: "بازگردانی / فعال‌سازی مجدد", icon: <RollbackOutlined />,
            onClick: () => restore(r.id), hidden: !(r.status === "cancelled" && can("sc.event.create")) },
          { key: "approve", label: "تأیید و ثبت در دفتر امانی", icon: <CheckOutlined />, primary: true,
            onClick: () => approve(r.id),
            hidden: !(r.status !== "confirmed" && r.status !== "cancelled" && can("sc.event.approve")) },
          { key: "cancel", label: "لغو / ابطال", icon: <StopOutlined />, danger: true,
            confirm: "این رویداد لغو/ابطال شود؟ (اثر دفتر برمی‌گردد)", onClick: () => cancel(r.id),
            hidden: !(r.status === "confirmed" && can("sc.event.approve")) },
          { key: "delete", label: "حذف", icon: <DeleteOutlined />, danger: true,
            confirm: "این رویداد حذف شود؟", onClick: () => removeEvent(r.id),
            hidden: !(r.status !== "confirmed" && can("sc.event.create")) },
        ]} />
      ),
    },
  ];

  const eventsSummary = [
    { dataIndex: "weight_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "roll_count", type: "sum", format: (v) => faNum(v) },
    { dataIndex: "carton_count", type: "sum", format: (v) => faNum(v) },
    { dataIndex: "unit_price", type: "wavg", weightKey: "weight_kg", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "total_amount", type: "sum", format: (v) => fmtRial(Math.round(v)) },
    { dataIndex: "color", type: "unique" },
    { dataIndex: "event_type", type: "unique", labelOf: eventLabel },
    { dataIndex: "status", type: "unique", labelOf: (v) => statusMeta(v).l },
  ];

  // ---- رندرِ فیلدهای فرم بر اساس قالب ----
  const isReq = (name) => (tpl.required || []).includes(name);
  const rules = (name) => isReq(name) ? [{ required: true, message: "الزامی است" }] : [];

  const partySelect = (cfg) => (
    <Form.Item name={cfg.name} label={cfg.label} rules={rules(cfg.name)}>
      <PartyPicker types={cfg.types} parties={parties} defaultHint={cfg.defaultHint}
        onCreated={(p) => setParties((ps) => [...ps, p])} />
    </Form.Item>
  );

  const renderField = (name) => {
    const common = { name, rules: rules(name) };
    switch (name) {
      case "source_purchase_id":
        return <Form.Item name="source_purchase_id" label="سندِ خریدِ منبع (از موجودیِ خریداری‌شده)">
          <Select allowClear showSearch optionFilterProp="label" size="large" onChange={onPickPurchase}
            placeholder="انتخابِ سندِ خرید (اختیاری) — مانده را پر می‌کند"
            options={purchases.map((p) => ({ value: p.id,
              label: `${p.event_code} — ${p.yarn_type || "نخ"} — مانده ${p.remaining_kg} کیلو${p.permit_number ? " — مجوز " + p.permit_number : ""}` }))} />
        </Form.Item>;
      case "item_type_id":
        return <Form.Item {...common} label="نوع کالا">
          <Select allowClear placeholder="نوع کالا" size="large" options={items.map((i) => ({ value: i.id, label: i.name }))} /></Form.Item>;
      case "weight_kg":
        return <Form.Item {...common} label="وزن (کیلوگرم)"><InputNumber min={0.001} step={1} style={{ width: "100%" }} size="large" /></Form.Item>;
      case "carton_count":
        return <Form.Item {...common} label="تعداد کارتن"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>;
      case "pallet_count":
        return <Form.Item {...common} label="تعداد پالت"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>;
      case "permit_number":
        return <Form.Item {...common} label="شماره مجوز"><Input size="large" placeholder="مثلاً MJ-123" /></Form.Item>;
      case "yarn_count":
        return <Form.Item {...common} label="نمرهٔ نخ"><Input size="large" /></Form.Item>;
      case "twist":
        return <Form.Item {...common} label="تاب"><Input size="large" /></Form.Item>;
      case "fabric_type":
        return <Form.Item {...common} label="نوع پارچه"><Input size="large" /></Form.Item>;
      case "color":
        return <Form.Item {...common} label="رنگ"><Input size="large" /></Form.Item>;
      case "roll_count":
        return <Form.Item {...common} label="تعداد طاقه"><InputNumber min={0} style={{ width: "100%" }} size="large" /></Form.Item>;
      case "operation_code":
        return <Form.Item {...common} label="کد عملیات"><Input size="large" /></Form.Item>;
      case "order_code":
        return <Form.Item {...common} label="شماره سفارش"><Input size="large" /></Form.Item>;
      case "external_invoice_number":
        return <Form.Item {...common} label="شماره فاکتور (سریِ فروشنده)"><Input size="large" /></Form.Item>;
      case "external_document_number":
        return <Form.Item {...common} label="شماره سند (داخلی)"><Input size="large" /></Form.Item>;
      case "unit_price":
        return <Form.Item {...common} label="فی واحد (ریال)"><InputNumber min={0} style={{ width: "100%" }} size="large"
          formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} /></Form.Item>;
      case "total_amount":
        return <Form.Item {...common} label="مبلغ کل (ریال)"><InputNumber min={0} style={{ width: "100%" }} size="large"
          formatter={(x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")} /></Form.Item>;
      case "notes":
        return <Form.Item {...common} label="توضیحات"><Input.TextArea rows={2} /></Form.Item>;
      case "reason":
        return <Form.Item {...common} label="دلیل / شرح اصلاح"><Input.TextArea rows={2} /></Form.Item>;
      default:
        return null;
    }
  };

  const fixedSrcParty = tpl.fixedSourceType ? partyOfType(tpl.fixedSourceType) : null;
  const fixedDstParty = tpl.fixedDestType ? partyOfType(tpl.fixedDestType) : null;
  const mainPhysical = (tpl.main || []).filter((f) => !PARTY_FIELDS.includes(f));

  const isKnit = evType === "raw_fabric_sent_to_dyeing" || evType === "raw_fabric_produced";
  const numFmt = (x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const accountingBlock = (
    <div>
      <Form.Item name="unit_price" label={isKnit ? "فی خدمات (هر کیلو، ریال)" : "فی واحد (هر کیلو، ریال)"}>
        <InputNumber min={0} style={{ width: "100%" }} size="large" formatter={numFmt} />
      </Form.Item>
      <Form.Item name="manual_total_override" valuePropName="checked" style={{ marginBottom: 6 }}>
        <Checkbox>ویرایش دستیِ مبلغ</Checkbox>
      </Form.Item>
      {wOverride && <div style={{ color: "#d46b08", fontSize: 12, marginBottom: 6 }}>⚠ مبلغ دستی وارد می‌شود (محاسبهٔ خودکار غیرفعال است)</div>}
      <Form.Item name="total_amount" label={isKnit ? "جمع خدمات (ریال)" : "جمع مبلغ (ریال)"}>
        <InputNumber min={0} disabled={!wOverride} style={{ width: "100%" }} size="large" formatter={numFmt} />
      </Form.Item>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#107C41", textAlign: "left" }}>
        {isKnit ? "جمع خدمات: " : "جمع مبلغ: "}{fmtRial(wTotal)}
      </div>
    </div>
  );

  const collapseItems = useMemo(() => {
    const its = [];
    if ((tpl.advanced || []).length)
      its.push({ key: "adv", label: "اطلاعات تکمیلی", children: <div>{tpl.advanced.map((f) => <div key={f}>{renderField(f)}</div>)}</div> });
    if ((tpl.docs || []).length)
      its.push({ key: "docs", label: "سند و توضیحات", children: <div>{tpl.docs.map((f) => <div key={f}>{renderField(f)}</div>)}</div> });
    if ((tpl.accounting || []).length)
      its.push({ key: "acc", label: "اطلاعات مالی (اختیاری)", children: accountingBlock });
    return its;
    // eslint-disable-next-line
  }, [evType, items, parties, wOverride, wTotal]);

  const formBody = (
    <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
      <Form.Item name="event_type" label={
        <Space>نوع حرکت
          <Button type="link" size="small" icon={<QuestionCircleOutlined />} onClick={() => setHelpOpen(true)}>
            راهنمای انتخاب نوع عملیات
          </Button>
        </Space>
      } rules={[{ required: true }]}>
        <Select size="large" options={typeOptions} />
      </Form.Item>
      <Form.Item name="event_date" label="تاریخ (خالی = امروز)" rules={[{ required: true, message: "تاریخ را انتخاب کنید" }]}>
        <JalaliDateInput />
      </Form.Item>

      {/* «دلیلِ ویرایش» طبقِ خواستِ کاربر حذف شد */}

      {/* مبدأِ ثابت (فقط نمایش) */}
      {fixedSrcParty && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">مبدأ (ثابت): </Text>
          <Tag color="blue" style={{ fontSize: 13 }}>{fixedSrcParty.name}</Tag>
        </div>
      )}
      {tpl.sourceFixedLabel && (
        <div style={{ marginBottom: 12 }}><Text type="secondary">مبدأ: </Text><Tag>{tpl.sourceFixedLabel}</Tag></div>
      )}

      {/* انتخابِ مبدأ/نگه‌دارنده/مقصد طبق قالب */}
      {tpl.sourceSelect && partySelect(tpl.sourceSelect)}
      {tpl.holderSelect && partySelect(tpl.holderSelect)}
      {tpl.destSelect && partySelect(tpl.destSelect)}

      {/* مقصدِ ثابت (فقط نمایش) */}
      {fixedDstParty && (
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">مقصد (ثابت): </Text>
          <Tag color="green" style={{ fontSize: 13 }}>{fixedDstParty.name}</Tag>
        </div>
      )}

      {/* فیلدهای اصلیِ همان قالب */}
      {mainPhysical.map((f) => <div key={f}>{renderField(f)}</div>)}

      {/* بخش‌های تاشو: تکمیلی / سند / مالی */}
      {collapseItems.length > 0 && <Collapse items={collapseItems} style={{ marginTop: 4 }} />}
    </Form>
  );

  const footer = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <Space size={4}>
        {editingId != null && (
          <>
            <Tooltip title="رکورد قبلی (Alt+→)">
              <Button icon={<RightOutlined />} disabled={editIndex <= 0} onClick={() => goEdit(-1)} />
            </Tooltip>
            <Tooltip title="رکورد بعدی (Alt+←)">
              <Button icon={<LeftOutlined />} disabled={editIndex < 0 || editIndex >= rows.length - 1} onClick={() => goEdit(1)} />
            </Tooltip>
            {editIndex >= 0 && <Text type="secondary" style={{ fontSize: 12 }}>{`${editIndex + 1} از ${rows.length}`}</Text>}
          </>
        )}
      </Space>
      <Space size={8}>
        <Button onClick={() => setOpen(false)}>انصراف</Button>
        {editingId != null && editIndex < rows.length - 1 && (
          <Button onClick={saveAndNext}>ذخیره و بعدی</Button>
        )}
        <Button type="primary" icon={<CheckOutlined />} onClick={submit}>{editingId != null ? "ذخیره" : "ثبت پیش‌نویس"}</Button>
      </Space>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ margin: 0 }}>فاکتورها</h2>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
          <Button type="primary" ghost icon={<DownloadOutlined />} onClick={() => setExportOpen(true)}>خروجی سفارشی</Button>
          <Button icon={<DownloadOutlined />} onClick={async () => {
            if (!filteredRows.length) {
              message.warning("ردیفی برای خروجی نیست");
              return;
            }
            try {
              const kind = await exportEventsForImport(filteredRows);
              message.success(kind === "xlsx"
                ? "فایل Excel سازگار با ایمپورت دانلود شد — در مرکز ایمپورت با قالب «همگانی» آپلود کنید"
                : "فایل CSV دانلود شد — در مرکز ایمپورت با قالب «همگانی» آپلود کنید");
            } catch (e) {
              message.error(e?.message || "خروجی ناموفق بود");
            }
          }}>خروجی برای ایمپورت</Button>
          <Button onClick={() => navigate("/supply-chain/import")}>ایمپورت</Button>
          {can("sc.event.create") && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/supply-chain/new")}>
              ثبت فاکتور جدید
            </Button>
          )}
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Select allowClear placeholder="دسته" style={{ width: 140 }} value={filters._category}
            onChange={(v) => {
              const types = v ? eventTypesForCategory(v) : null;
              setFilters((f) => ({
                ...f, _category: v || undefined,
                event_type: types && types.length === 1 ? types[0] : undefined,
                _catTypes: types || undefined,
              }));
            }}
            options={enabledCategories().map((c) => ({ value: c.key, label: c.label }))} />
          <Select allowClear placeholder="نوع عملیات" style={{ width: 200 }} value={filters.event_type}
            onChange={(v) => setFilters((f) => ({ ...f, event_type: v }))}
            options={(filters._catTypes
              ? FILTERABLE_EVENT_TYPES.filter((e) => filters._catTypes.includes(e.v))
              : FILTERABLE_EVENT_TYPES).map((e) => ({ value: e.v, label: e.l }))} />
          <Select allowClear placeholder="طرف حساب" style={{ width: 160 }} value={filters.party_id}
            onChange={(v) => setFilters((f) => ({ ...f, party_id: v }))}
            showSearch optionFilterProp="children">
            {parties.map((p) => <Option key={p.id} value={p.id}>{p.name}</Option>)}
          </Select>
          <Input allowClear placeholder="شماره فاکتور" style={{ width: 140 }}
            value={filters.q_invoice || ""}
            onChange={(e) => setFilters((f) => ({ ...f, q_invoice: e.target.value || undefined }))} />
          <Select allowClear placeholder="وضعیت" style={{ width: 160 }}
            value={filters.needs_completion ? "needs_completion" : filters.status}
            onChange={(v) => setFilters((f) => v === "needs_completion"
              ? { ...f, status: undefined, needs_completion: true }
              : { ...f, status: v, needs_completion: undefined })}
            options={[
              { value: "confirmed", label: "تأییدشده" },
              { value: "draft", label: "پیش‌نویس" },
              { value: "needs_completion", label: "نیازمند تکمیل / ناقص" },
              { value: "needs_review", label: "نیازمند بررسی" },
              { value: "disputed", label: "مغایر" },
              { value: "cancelled", label: "لغوشده" },
            ]} />
          <Space size={4}>
            <span style={{ fontSize: 12, color: "#888" }}>از</span>
            <JalaliDateInput value={filters.date_from || null}
              onChange={(v) => setFilters((f) => ({ ...f, date_from: v || undefined }))} />
            <span style={{ fontSize: 12, color: "#888" }}>تا</span>
            <JalaliDateInput value={filters.date_to || null}
              onChange={(v) => setFilters((f) => ({ ...f, date_to: v || undefined }))} />
          </Space>
        </Space>
      </Card>

      <Card>
        <EditableTable
          layoutId="sc-events"
          dataSource={filteredRows}
          columns={isMobile
            ? columns.filter((c) => ["event_date", "item_name", "weight_kg", "total_amount", "status"].includes(c.dataIndex) || c.key === "actions" || c.key === "category")
            : columns}
          rowKey="id" loading={loading}
          pagination={{ pageSize: 20 }}
          gxSummary={eventsSummary}
          canEditRow={canEditEventRow}
          onSaveMany={async (items) => {
            await scBulkUpdateEvents(items);
            await load();
          }}
          onDeleteMany={async (ids) => {
            const r = await scBulkDeleteEvents(ids);
            message.success(r.data.message);
            await load();
          }} />
      </Card>

      {/* موبایل: Drawer تمام‌قد + دکمهٔ ثابتِ پایین؛ دسکتاپ: Modal */}
      {isMobile ? (
        <Drawer title={tpl.label || "ثبت حرکت جدید"} open={open} onClose={() => setOpen(false)}
          placement="bottom" height="92%" footer={footer} styles={{ body: { paddingBottom: 12 } }}>
          {formBody}
        </Drawer>
      ) : (
        <Modal title={(editingId ? "ویرایش — " : "") + (tpl.label || "ثبت حرکت جدید")} open={open} onCancel={() => setOpen(false)}
          footer={footer} width={620}>
          {formBody}
        </Modal>
      )}

      <ScExportModal open={exportOpen} onClose={() => setExportOpen(false)} parties={parties}
        initial={{ date_from: filters.date_from, date_to: filters.date_to, party_id: filters.party_id }} />

      {/* راهنمای انتخاب نوع عملیات (#9) */}
      <Modal title="راهنمای انتخاب نوع عملیات" open={helpOpen} onCancel={() => setHelpOpen(false)} footer={null} width={560}>
        <div style={{ direction: "rtl", lineHeight: 1.9 }}>
          {MOVEMENT_GUIDE.map(([t, d]) => (
            <div key={t} style={{ marginBottom: 10 }}>
              <b style={{ color: "#1F4E79" }}>{t}:</b> <span style={{ color: "#555" }}>{d}</span>
            </div>
          ))}
        </div>
      </Modal>

    </div>
  );
}
